import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Loader2, CheckCircle2, AlertCircle, Lock } from 'lucide-react';
import { usePDF } from '../context/PDFContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const UploadZone = ({ onUploadComplete }) => {
  const { setPdfInfo, setPages } = usePDF();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [uploadState, setUploadState] = useState('idle'); // idle | uploading | processing | done | error
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const onDrop = useCallback(async (acceptedFiles) => {
    // Auth-First: block upload for guests and route to /login.
    if (!user) {
      toast.info('Please sign in to upload a PDF');
      navigate('/login');
      return;
    }
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      setUploadState('error');
      setErrorMessage('Only PDF files are supported');
      toast.error('Please upload a PDF file');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadState('error');
      setErrorMessage(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum: 20MB`);
      toast.error('File too large. Maximum size is 20MB');
      return;
    }

    setUploadState('uploading');
    setUploadProgress(0);
    setErrorMessage('');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / (e.total || 1));
          setUploadProgress(pct);
          if (pct >= 100) {
            setUploadState('processing');
          }
        },
      });

      setUploadState('processing');
      setPdfInfo(response.data);

      const pagesResponse = await axios.get(`${API}/pdf/pages/${response.data.id}`);
      setPages(pagesResponse.data.pages);

      setUploadState('done');
      toast.success('PDF uploaded successfully!');
      // Brief delay to show success state, then navigate
      setTimeout(() => {
        onUploadComplete && onUploadComplete(response.data);
      }, 500);
    } catch (error) {
      setUploadState('error');
      const msg = error.response?.data?.detail || 'Failed to upload PDF';
      setErrorMessage(msg);
      toast.error(msg);
    }
  }, [setPdfInfo, setPages, onUploadComplete, user, navigate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    disabled: uploadState === 'uploading' || uploadState === 'processing' || !user,
    noClick: !user,
    noKeyboard: !user,
    noDrag: !user,
    maxSize: MAX_FILE_SIZE,
  });

  const renderStatus = () => {
    switch (uploadState) {
      case 'uploading':
        return (
          <div className="flex flex-col items-center gap-3" data-testid="upload-progress">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
            <p className="text-lg font-semibold">Uploading file... {uploadProgress}%</p>
            <div className="w-64 h-2 bg-border rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        );
      case 'processing':
        return (
          <div className="flex flex-col items-center gap-3" data-testid="upload-processing">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
            <p className="text-lg font-semibold">Processing PDF...</p>
            <p className="text-sm text-muted-foreground">Generating page previews</p>
          </div>
        );
      case 'done':
        return (
          <div className="flex flex-col items-center gap-3" data-testid="upload-done">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
            <p className="text-lg font-semibold text-green-600">Loaded successfully!</p>
          </div>
        );
      case 'error':
        return (
          <div className="flex flex-col items-center gap-3" data-testid="upload-error">
            <AlertCircle className="w-10 h-10 text-destructive" />
            <p className="text-lg font-semibold text-destructive">Upload Failed</p>
            <p className="text-sm text-muted-foreground">{errorMessage}</p>
            <button onClick={() => setUploadState('idle')} className="text-sm text-primary underline mt-1">Try again</button>
          </div>
        );
      default:
        if (!user && !authLoading) {
          return (
            <>
              <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                <Lock className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight text-foreground mb-3">
                Sign in to upload
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mb-6 text-center max-w-md">
                Please sign in with Google to upload and edit your PDF securely.
              </p>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate('/login'); }}
                data-testid="upload-signin-btn"
                className="px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
              >
                Sign in
              </button>
              <p className="mt-4 text-xs text-muted-foreground">Free tier: 1 download / day.</p>
            </>
          );
        }
        return (
          <>
            <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
              <FileText className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight text-foreground mb-3">
              {isDragActive ? 'Drop your PDF here' : 'Upload your PDF'}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mb-6 text-center max-w-md">
              Drag and drop your PDF file here, or click to browse
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <FileText className="w-4 h-4" />
              <span>PDF files only. Maximum 20MB.</span>
            </div>
          </>
        );
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div
        {...getRootProps()}
        data-testid="upload-dropzone"
        className={`relative w-full max-w-2xl h-96 border-2 border-dashed rounded-xl transition-all duration-200 ${
          isDragActive
            ? 'border-primary bg-primary/5 scale-[1.02]'
            : uploadState === 'error'
              ? 'border-destructive bg-destructive/5'
              : 'border-border hover:border-primary/50 hover:bg-accent/50 cursor-pointer'
        } ${uploadState === 'uploading' || uploadState === 'processing' ? 'pointer-events-none' : ''}`}
      >
        <input {...getInputProps()} data-testid="file-input" />
        <div className="absolute inset-0 flex flex-col items-center justify-center p-16">
          {renderStatus()}
        </div>
      </div>
    </div>
  );
};

export default UploadZone;
